import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { site as defaults } from '../config/site';
import { api } from '../lib/api';

const SiteContext = createContext(null);

// deep merge - override with backend content when present
function deepMerge(target, source) {
  if (Array.isArray(source)) return source; // arrays: replace entirely (simpler for CMS)
  if (source && typeof source === 'object') {
    const out = { ...(target || {}) };
    for (const [k, v] of Object.entries(source)) {
      if (v && typeof v === 'object' && !Array.isArray(v) && target && typeof target[k] === 'object') {
        out[k] = deepMerge(target[k], v);
      } else if (v !== undefined && v !== null) {
        out[k] = v;
      }
    }
    return out;
  }
  return source ?? target;
}

export function SiteProvider({ children }) {
  const [override, setOverride] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/content');
      setOverride(data?.data || {});
    } catch (e) {
      setOverride({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const merged = useMemo(() => deepMerge(defaults, override || {}), [override]);

  const value = useMemo(() => ({ site: merged, loading, refresh: load, setOverride }), [merged, loading, load]);

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite() {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error('useSite must be used within SiteProvider');
  return ctx.site;
}

export function useSiteMeta() {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error('useSiteMeta must be used within SiteProvider');
  return ctx;
}
